import {
  BOT_CHESS_THINK_TICKS,
  BOT_DECISION_TICKS,
  BOT_DODGE_CHANCE,
  BOT_FIRE_CHANCE,
  BOT_FIRE_RANGE,
  BOT_LEAD,
  BOT_PREFERRED_DIST,
  BOT_STRAFE,
  TILE,
  WEAPONS,
} from './constants';
import { toMove } from './chess';
import { chessBotMove } from './chessBot';
import { pathDirection, perpendicular, steerClear } from './navigation';
import { hasLineOfSight } from './obstacles';
import { hashRandom } from './rng';
import type { Input, PlayerId, State, Vec2 } from './types';
import { add, dist, normalize, scale, sub } from './vec';
import { opponentOf } from './weapons';

/** Anything that produces a player's input for a tick: a human's controls or the bot. */
export type InputSource = (state: State, playerId: PlayerId) => Input;

// The v1 bot is a pure function of the state: no memory, no clock, no Math.random. Its random
// choices come from a hash of the state's RNG and the current decision slot, so a replay of the same
// states yields the same inputs. It plays both phases: the arena below, the board in chessBot.ts.

function hash(s: State, salt: number): number {
  return hashRandom(s.rng, salt);
}

function slotHash(s: State, salt: number): number {
  return hash(s, Math.floor(s.tick / BOT_DECISION_TICKS) * 7 + salt);
}

function tickHash(s: State, salt: number): number {
  return hash(s, s.tick * 13 + salt + 1000);
}

/** Is an enemy projectile about to cross the bot's position? */
function incomingThreat(s: State, me: PlayerId): Vec2 | null {
  const pos = s.players[me].pos;
  for (const p of s.projectiles) {
    if (p.ownerId === me) continue;
    const toMe = sub(pos, p.pos);
    const d = dist(pos, p.pos);
    if (d > 6 * TILE) continue;
    const dir = normalize(p.vel);
    const along = toMe.x * dir.x + toMe.y * dir.y;
    if (along <= 0) continue;
    const off = Math.abs(toMe.x * dir.y - toMe.y * dir.x);
    if (off < 2 * TILE) return dir;
  }
  return null;
}

export const botInput: InputSource = (s, me) => (s.transitionTicks > 0 ? { seq: 0, moveDir: { x: 0, y: 0 } } : s.phase === 'chess' ? boardInput(s, me) : arenaInput(s, me));

/**
 * At the board the bot waits BOT_CHESS_THINK_TICKS after the previous move (or the start of the
 * phase) and then plays the search's move. Not to move, match over, or the phase ending with this
 * very tick (the move would be ignored): nothing, and no search.
 */
function boardInput(s: State, me: PlayerId): Input {
  const input: Input = { seq: s.tick, moveDir: { x: 0, y: 0 } };
  if (s.winner !== null || s.tick >= s.phaseEndsAtTick) return input;
  if (toMove(s) !== me || s.tick - s.chess.lastMoveTick < BOT_CHESS_THINK_TICKS) return input;
  const move = chessBotMove(s);
  if (move) input.chessMove = move;
  return input;
}

function arenaInput(s: State, me: PlayerId): Input {
  const self = s.players[me];
  const foe = s.players[opponentOf(me)];
  // Helper boxes are immovable obstacles, just like pillars, until shot open.
  const navigation = { ...s, pillars: [...s.pillars, ...s.npcBoxes] };
  const side = slotHash(s, 1) < 0.5 ? 1 : -1;
  let move: Vec2;
  let fireAt: Vec2 | undefined;

  const threat = incomingThreat(s, me);
  if (threat) {
    move = perpendicular(threat, side);
  } else if (self.weapon === null) {
    // unarmed: fetch the nearest pickup, else roam towards the nearest quality marker
    const targets = s.pickups.length > 0 ? s.pickups.map((p) => p.pos) : s.spawnSpots.map((spot) => spot.pos);
    let goal: Vec2 | null = null;
    let best = Infinity;
    for (const t of targets) {
      const d = dist(self.pos, t);
      if (d < best) {
        best = d;
        goal = t;
      }
    }
    move = goal && best > 1 ? pathDirection(navigation, self.pos, goal) : { x: 0, y: 0 };
  } else {
    const d = dist(self.pos, foe.pos);
    const toFoe = normalize(sub(foe.pos, self.pos));
    const seesFoe = hasLineOfSight(s.map, s.pillars, self.pos, foe.pos);
    if (slotHash(s, 2) < BOT_DODGE_CHANCE) {
      move = perpendicular(toFoe, side);
    } else if (!seesFoe) {
      move = pathDirection(navigation, self.pos, foe.pos);
    } else {
      const approach = d > BOT_PREFERRED_DIST ? 1 : d < BOT_PREFERRED_DIST * 0.6 ? -1 : 0;
      move = normalize(add(scale(toFoe, approach), scale(perpendicular(toFoe, side), BOT_STRAFE)));
    }
    if (seesFoe && d <= BOT_FIRE_RANGE && tickHash(s, 3) < BOT_FIRE_CHANCE) {
      const spec = WEAPONS[self.weapon];
      const flight = spec.kind === 'projectile' ? d / spec.projectileSpeed : 0;
      fireAt = add(foe.pos, scale(foe.vel, flight * BOT_LEAD));
    }
  }

  move = steerClear(navigation, self.pos, self.radius, move);
  const input: Input = { seq: s.tick, moveDir: normalize(move) };
  if (fireAt) input.fireAt = fireAt;
  return input;
}
